import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bot, KeyRound, Copy, Ban, Loader2, ShieldCheck, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { getSessionToken } from "@/utils/auth";

const BACKEND = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${getSessionToken()}` } });
const when = (s) => (s ? new Date(s).toLocaleString() : "Never");

function NewKeyBanner({ created, onClose }) {
  return (
    <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 space-y-2" data-testid="new-ai-key-banner">
      <div className="font-semibold text-amber-900">Copy this key now. It will never be shown again.</div>
      <code className="block break-all select-all bg-white border rounded p-2 text-xs" data-testid="new-ai-key-value">{created.key}</code>
      <div className="flex gap-2">
        <button onClick={() => { navigator.clipboard.writeText(created.key); toast.success("Copied"); }}
          className="h-9 px-3 rounded-lg bg-zinc-900 text-white text-xs font-semibold flex items-center gap-1" data-testid="copy-ai-key">
          <Copy className="w-3.5 h-3.5" /> Copy key
        </button>
        <button onClick={onClose} className="h-9 px-3 rounded-lg border text-xs font-semibold" data-testid="dismiss-ai-key">I saved it</button>
      </div>
      <p className="text-[11px] text-amber-800">Store it only in Grok's / your agent's secret settings. Never paste it into a website or text message.</p>
    </div>
  );
}

const SCOPE_LABELS = {
  "sms:draft": "Draft texts (you approve each one)",
  "followups:pause": "Pause automated follow-ups",
  "growth_board:read": "Read Growth Board",
  "growth_board:write": "Edit Growth Board (create, update, comment, archive — no delete)",
};
const CPO_NAME = "Grok - Chief Project Officer";
const CONTENT_NAME = "Head Of Content";

function ScopeToggles({ k, onChange }) {
  if (k.revoked_at) return null;
  if (k.scopes.length === 1 && k.scopes[0] === "reviews") {
    return <div className="mt-2 text-xs text-zinc-500" data-testid={`ai-key-scopes-${k.key_id}`}>Review tracking only (no other CRM access)</div>;
  }
  const toggle = (scope) => {
    const next = k.scopes.includes(scope) ? k.scopes.filter((s) => s !== scope) : [...k.scopes, scope];
    onChange(k, next);
  };
  return (
    <div className="mt-2 flex flex-col gap-1" data-testid={`ai-key-scopes-${k.key_id}`}>
      <label className="flex items-center gap-2 text-xs text-zinc-500"><input type="checkbox" checked disabled /> Read CRM data (always on)</label>
      {Object.entries(SCOPE_LABELS).map(([scope, label]) => (
        <label key={scope} className="flex items-center gap-2 text-xs text-zinc-700 cursor-pointer">
          <input type="checkbox" checked={k.scopes.includes(scope)} onChange={() => toggle(scope)}
            data-testid={`ai-scope-${k.key_id}-${scope.replace(":", "-")}`} />
          {label}
        </label>
      ))}
    </div>
  );
}

function KeyRow({ k, onRevoke, onScopes }) {
  return (
    <div className="p-3 flex items-start justify-between gap-3" data-testid={`ai-key-${k.key_id}`}>
      <div className="min-w-0">
        <div className="font-medium text-zinc-900 flex items-center gap-2 min-w-0">
          <span className="break-all">{k.name}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase ${k.revoked_at ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
            {k.revoked_at ? "Revoked" : k.scopes.join(", ")}
          </span>
        </div>
        <div className="text-[11px] text-zinc-500 font-mono">{k.prefix}…</div>
        <div className="text-[11px] text-zinc-400">Created {when(k.created_at)} · Last used {when(k.last_used_at)}</div>
        <div className="text-[11px] text-zinc-400 font-mono">{k.key_id}</div>
        <ScopeToggles k={k} onChange={onScopes} />
      </div>
      {!k.revoked_at && (
        <button onClick={() => onRevoke(k)} className="h-8 px-2 rounded border border-red-200 text-red-700 text-xs font-semibold flex items-center gap-1 shrink-0"
          data-testid={`revoke-ai-key-${k.key_id}`}>
          <Ban className="w-3.5 h-3.5" /> Revoke
        </button>
      )}
    </div>
  );
}

function AuditLog({ audit }) {
  if (!audit) return null;
  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid="ai-audit-log">
      <div className="p-3 border-b flex justify-between text-sm">
        <span className="font-semibold">Activity log</span>
        <span className="text-zinc-500">{audit.last_24h} calls in 24h · {audit.total} total</span>
      </div>
      <div className="divide-y max-h-96 overflow-y-auto">
        {audit.data.length === 0 && <div className="p-4 text-sm text-zinc-400 text-center">No activity yet</div>}
        {audit.data.map((r, i) => (
          <div key={i} className="px-3 py-2 text-xs flex justify-between gap-2">
            <span className="truncate">
              <span className={`font-semibold ${r.status >= 400 ? "text-red-600" : "text-green-700"}`}>{r.status}</span>{" "}
              <span className="font-mono">{r.method} {r.path.replace("/api/ai/v1", "")}</span>
              {r.query && Object.keys(r.query).length > 0 && <span className="text-zinc-400"> ?{new URLSearchParams(r.query).toString()}</span>}
            </span>
            <span className="text-zinc-400 shrink-0">{r.key_name || "unknown key"} · {new Date(r.created_at).toLocaleString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AIIntegration() {
  const [keys, setKeys] = useState(null);
  const [audit, setAudit] = useState(null);
  const [name, setName] = useState("Grok - Head of Sales");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [showRevoked, setShowRevoked] = useState(false);
  const revokedCount = (keys || []).filter((k) => k.revoked_at).length;
  const visibleKeys = (keys || []).filter((k) => showRevoked || !k.revoked_at);

  const load = () => {
    axios.get(`${API}/admin/ai-keys`, auth()).then((r) => setKeys(r.data)).catch(() => setKeys([]));
    axios.get(`${API}/admin/ai-audit?limit=100`, auth()).then((r) => setAudit(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const create = async (preset) => {
    setCreating(true);
    try {
      const body = preset || { name };
      const { data } = await axios.post(`${API}/admin/ai-keys`, body, auth());
      setCreated(data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't create key");
    } finally {
      setCreating(false);
    }
  };

  const setScopes = async (k, scopes) => {
    try {
      await axios.put(`${API}/admin/ai-keys/${k.key_id}/scopes`, { scopes }, auth());
      toast.success("Permissions updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update permissions");
    }
    load();
  };

  const revoke = async (k) => {
    if (!window.confirm(`Revoke "${k.name}"? Anything using it stops working immediately.`)) return;
    try {
      await axios.post(`${API}/admin/ai-keys/${k.key_id}/revoke`, {}, auth());
      toast.success("Key revoked");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't revoke key");
    }
    load();
  };

  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-zinc-50">
      <Header />
      <div className="max-w-2xl mx-auto px-4 py-5 space-y-4">
        <button onClick={() => navigate("/settings")} data-testid="back-to-settings"
          className="flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900">
          <ArrowLeft className="w-4 h-4" /> Back to Settings
        </button>
        <div>
          <h1 className="text-xl font-bold text-zinc-900 flex items-center gap-2"><Bot className="w-6 h-6 text-[#207024]" /> AI Integration (Grok)</h1>
          <p className="text-xs text-zinc-500 mt-1">Secure API access for your AI agent. Every call is logged below.</p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-xs text-green-900 flex gap-2">
          <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
          <span><strong>Read access + approved texting.</strong> The AI can read your CRM data. With "Draft texts" on, it can only write drafts, and nothing sends until you approve it in <a href="/ai-texts" className="underline font-semibold">AI Text Approvals</a>. It cannot book, reschedule or edit estimates.</span>
        </div>

        {created && <NewKeyBanner created={created} onClose={() => setCreated(null)} />}

        <div className="bg-white rounded-xl border p-3 space-y-2">
          <div className="text-sm font-semibold flex items-center gap-1.5"><KeyRound className="w-4 h-4" /> Create API key</div>
          <div className="flex gap-2">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Key name"
              className="flex-1 h-10 border rounded-lg px-3 text-sm" data-testid="ai-key-name" />
            <button onClick={() => create()} disabled={creating || !name.trim()}
              className="h-10 px-4 rounded-lg bg-[#207024] text-white text-sm font-semibold disabled:opacity-50 flex items-center gap-1" data-testid="create-ai-key">
              {creating && <Loader2 className="w-4 h-4 animate-spin" />} Create
            </button>
          </div>
          {!(keys || []).some((k) => !k.revoked_at && k.name === CPO_NAME) && (
            <button
              onClick={() => create({ name: CPO_NAME, scopes: ["growth_board:read", "growth_board:write"],
                rate_limit_per_minute: (keys || []).find((k) => !k.revoked_at)?.rate_limit_per_minute || 120 })}
              disabled={creating} data-testid="create-cpo-key"
              className="mt-3 w-full h-10 rounded-lg border-2 border-[#207024] text-[#207024] text-sm font-semibold disabled:opacity-50">
              + Create &quot;{CPO_NAME}&quot; key (read + Growth Board)
            </button>
          )}
          {!(keys || []).some((k) => !k.revoked_at && k.name === CONTENT_NAME) && (
            <button onClick={() => create({ name: CONTENT_NAME, scopes: ["reviews"] })}
              disabled={creating} data-testid="create-content-key"
              className="mt-2 w-full h-10 rounded-lg border-2 border-[#207024] text-[#207024] text-sm font-semibold disabled:opacity-50">
              + Create &quot;{CONTENT_NAME}&quot; key (review tracking only)
            </button>
          )}
        </div>

        <div className="bg-white rounded-xl border divide-y" data-testid="ai-keys-list">
          {keys === null ? <div className="p-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin" /></div>
            : visibleKeys.length === 0 ? <div className="p-4 text-sm text-zinc-400 text-center">No active keys</div>
              : visibleKeys.map((k) => <KeyRow key={k.key_id} k={k} onRevoke={revoke} onScopes={setScopes} />)}
        </div>
        {revokedCount > 0 && (
          <button onClick={() => setShowRevoked(!showRevoked)} className="text-xs text-zinc-500 underline -mt-2" data-testid="toggle-revoked-keys">
            {showRevoked ? "Hide" : "Show"} {revokedCount} revoked key{revokedCount === 1 ? "" : "s"}
          </button>
        )}

        <div className="bg-white rounded-xl border p-3 text-xs space-y-1.5" data-testid="ai-connection-info">
          <div className="text-sm font-semibold">Connect Grok</div>
          <div>Base URL: <code className="select-all bg-zinc-100 px-1 rounded">{API}/ai/v1</code></div>
          <div>Header: <code className="bg-zinc-100 px-1 rounded">X-API-Key: &lt;your key&gt;</code></div>
          <div>Tool definitions (OpenAPI): <code className="select-all bg-zinc-100 px-1 rounded">{API}/ai/v1/openapi.json</code></div>
          <div className="text-zinc-500">Start with <code>/me</code>, then <code>/estimates?open_only=true</code>, <code>/customers/&#123;id&#125;</code>, <code>/availability/slots?zip_code=92562</code>.</div>
        </div>

        <AuditLog audit={audit} />
      </div>
    </div>
  );
}
